import React, { useState, useEffect } from 'react';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://myssa1.osc-fr1.scalingo.io/api';
const ROLES = ['Admin', 'Manager', 'Opérateur'];
const ROLE_COLORS = {
  Admin:      { bg: '#fde8e8', color: '#c0392b', border: '#e74c3c' },
  Manager:    { bg: '#e8f4fd', color: '#1a6fa8', border: '#3498db' },
  Opérateur:  { bg: '#e8fdf0', color: '#1a7a40', border: '#2ecc71' },
};

// ── Petits composants réutilisables ───────────────────────────────────────────

const RoleBadge = ({ role }) => {
  const c = ROLE_COLORS[role] || { bg: '#eee', color: '#555', border: '#ccc' };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
      background: c.bg, color: c.color, border: `1.5px solid ${c.border}`,
    }}>
      {role}
    </span>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  }}>
    <div style={{
      background: 'white', borderRadius: '12px', padding: '28px 32px',
      width: '440px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <p style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#2c3e50' }}>{title}</p>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#aaa' }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, ...props }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '5px' }}>{label}</label>
    <input style={{
      width: '100%', padding: '10px 12px', border: '1.5px solid #ddd',
      borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
    }} {...props} />
  </div>
);

const Btn = ({ children, variant = 'primary', onClick, disabled, style: s }) => {
  const base = {
    border: 'none', borderRadius: '7px', padding: '8px 16px',
    cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600,
    fontSize: '13px', transition: 'opacity 0.2s', opacity: disabled ? 0.6 : 1,
  };
  const variants = {
    primary: { background: '#3498db', color: 'white' },
    success: { background: 'linear-gradient(135deg, #3a7d44, #66bb6a)', color: 'white' },
    danger:  { background: '#e74c3c', color: 'white' },
    warning: { background: '#f39c12', color: 'white' },
    ghost:   { background: '#f0f0f0', color: '#555' },
  };
  return <button style={{ ...base, ...variants[variant], ...s }} onClick={onClick} disabled={disabled}>{children}</button>;
};

const Toast = ({ msg, onClose }) => (
  <div style={{
    position: 'fixed', top: '20px', right: '24px', zIndex: 2000,
    background: '#27ae60', color: 'white', padding: '12px 20px',
    borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    fontWeight: 600, fontSize: '14px', display: 'flex', gap: '12px', alignItems: 'center',
  }}>
    {msg}
    <span onClick={onClose} style={{ cursor: 'pointer', opacity: 0.7 }}>×</span>
  </div>
);

// ── Composant principal ───────────────────────────────────────────────────────

const Gestiondesdroits = () => {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [toast, setToast]     = useState(null);

  // Modals
  const [showCreate, setShowCreate]               = useState(false);
  const [showReset, setShowReset]                 = useState(null);
  const [showRole, setShowRole]                   = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);

  // Form créer
  const [newUser, setNewUser]         = useState({ username: '', email: '', role: 'Opérateur' });
  const [createError, setCreateError] = useState('');
  const [mailSentTo, setMailSentTo]   = useState(null);

  // Form reset mdp
  const [resetError, setResetError] = useState('');

  useEffect(() => {                                    // ← ajouter ici
    document.title = 'MYSSA – Gestion des droits';
  }, []);

  const showMsg = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  // ── GET — Charger les utilisateurs depuis PG ───────────────────────────────
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/utilisateurs`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setUsers(data.map(u => ({
        id:         u.id,
        username:   u.username,
        email:      u.email,
        role:       u.role,
        bloque:     u.bloque,
        tentatives: u.tentatives,
        actif:      u.actif,
        client_id:  u.client_id,
      })));
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // ── POST — Créer un utilisateur ────────────────────────────────────────────
const handleCreate = async () => {
  setCreateError('');
  if (!newUser.username.trim()) { setCreateError("Le nom d'utilisateur est obligatoire."); return; }
  if (!newUser.email.trim() || !newUser.email.includes('@')) { setCreateError('Email invalide.'); return; }

  try {
    const res = await fetch(`${API}/utilisateurs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: newUser.username, email: newUser.email, role: newUser.role }),
    });

    if (!res.ok) {
      const err = await res.json();
      setCreateError(err.error || "Erreur lors de la création ou de l'envoi du mail.");
      return; // ← on reste dans la modal avec le message d'erreur
    }

    await fetchUsers();
    const emailEnvoye = newUser.email;
    setShowCreate(false);
    setNewUser({ username: '', email: '', role: 'Opérateur' });
    setMailSentTo(emailEnvoye);
    setTimeout(() => setMailSentTo(null), 6000);

  } catch {
    setCreateError('Serveur inaccessible. Réessayez.');
  }
};

  // ── DELETE — Supprimer un utilisateur ─────────────────────────────────────
  const handleDelete = async (user) => {
    try {
      await fetch(`${API}/utilisateurs/${user.id}`, { method: 'DELETE' });
    } catch {}
    setUsers(prev => prev.filter(u => u.id !== user.id));
    setShowConfirmDelete(null);
    showMsg(`Utilisateur ${user.username} supprimé.`);
  };

  // ── PUT — Débloquer un utilisateur ────────────────────────────────────────
  const handleUnblock = async (user) => {
    try {
      await fetch(`${API}/utilisateurs/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bloque: false, tentatives: 0 }),
      });
    } catch {}
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, bloque: false, tentatives: 0 } : u));
    showMsg(`Compte de ${user.username} débloqué.`);
  };

  // ── PUT — Changer le rôle ─────────────────────────────────────────────────
  const handleRoleChange = async (user, newRole) => {
    try {
      await fetch(`${API}/utilisateurs/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
    } catch {}
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: newRole } : u));
    setShowRole(null);
    showMsg(`Rôle de ${user.username} changé en ${newRole}.`);
  };

  // ── POST — Renvoyer le lien d'invitation ──────────────────────────────────
  const handleResendLink = async (user) => {
    try {
      await fetch(`${API}/utilisateurs/${user.id}/resend-invite`, { method: 'POST' });
    } catch {}
    showMsg(`Lien renvoyé à ${user.email}.`);
  };

  // ── POST — Réinitialiser le mot de passe (envoie un lien par mail) ────────
  const handleResetPassword = async (user) => {
    setResetError('');
    try {
      await fetch(`${API}/utilisateurs/${user.id}/reset-password`, { method: 'POST' });
    } catch {}
    setShowReset(null);
    showMsg(`Un email de réinitialisation a été envoyé à ${user.email}.`);
  };

  const filtered = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '28px 32px', fontFamily: "'Segoe UI', sans-serif" }}>

      {toast && <Toast msg={toast} onClose={() => setToast(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#2c3e50' }}>👥 Gestion des droits</h1>
        <Btn variant="success" onClick={() => { setShowCreate(true); setCreateError(''); }}>+ Nouvel utilisateur</Btn>
      </div>

      {/* Bannière confirmation mail envoyé */}
      {mailSentTo && (
        <div style={{
          background: '#eafaf1', border: '1px solid #2ecc71', borderRadius: '8px',
          padding: '12px 16px', marginBottom: '16px', fontSize: '14px', color: '#1a7a40',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          ✅ Un email de création de mot de passe a été envoyé à <strong>{mailSentTo}</strong>.
          L'utilisateur dispose de 24h pour définir son mot de passe.
        </div>
      )}

      {/* Barre recherche + stats */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un utilisateur…"
          style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: '7px', fontSize: '14px', width: '240px' }}
        />
        <span style={{ fontSize: '13px', color: '#888' }}>
          {filtered.length} utilisateur{filtered.length > 1 ? 's' : ''}
          {users.filter(u => u.bloque).length > 0 && (
            <span style={{ color: '#e74c3c', marginLeft: '10px' }}>
              ⚠️ {users.filter(u => u.bloque).length} bloqué{users.filter(u => u.bloque).length > 1 ? 's' : ''}
            </span>
          )}
        </span>
      </div>

      {/* Tableau */}
      <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        {loading ? (
          <p style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Chargement…</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Utilisateur', 'Email', 'Rôle', 'Statut', 'Tentatives', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #e9ecef' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Aucun utilisateur trouvé</td></tr>
              )}
              {filtered.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #f0f0f0', background: user.bloque ? '#fff9f9' : 'white' }}>
                  <td style={{ padding: '13px 16px', fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      width: '32px', height: '32px', borderRadius: '50%', display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                      background: '#e8f4fb', color: '#1a6fa0', fontWeight: 700, fontSize: '13px',
                    }}>
                      {user.username[0].toUpperCase()}
                    </span>
                    {user.username}
                  </td>
                  <td style={{ padding: '13px 16px', color: '#666' }}>{user.email}</td>
                  <td style={{ padding: '13px 16px' }}><RoleBadge role={user.role} /></td>
                  <td style={{ padding: '13px 16px' }}>
                    {user.bloque
                      ? <span style={{ color: '#e74c3c', fontWeight: 700, fontSize: '13px' }}>🔒 Bloqué</span>
                      : <span style={{ color: '#27ae60', fontWeight: 700, fontSize: '13px' }}>✔ Actif</span>}
                  </td>
                  <td style={{ padding: '13px 16px', color: user.tentatives >= 2 ? '#e74c3c' : '#888', fontWeight: user.tentatives >= 2 ? 700 : 400 }}>
                    {user.tentatives} / 3
                  </td>
                  <td style={{ padding: '13px 16px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {user.bloque && (
                        <Btn variant="warning" onClick={() => handleUnblock(user)}>🔓 Débloquer</Btn>
                      )}
                      <Btn variant="ghost" onClick={() => setShowRole(user)}>✏️ Rôle</Btn>
                      <Btn variant="primary" onClick={() => { setShowReset(user); setResetError(''); }}>🔑 Réinit. mdp</Btn>
                      <Btn variant="ghost" onClick={() => handleResendLink(user)}>📧 Renvoyer lien</Btn>
                      <Btn variant="danger" onClick={() => setShowConfirmDelete(user)}>🗑</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal : Créer utilisateur ── */}
      {showCreate && (
        <Modal title="➕ Nouvel utilisateur" onClose={() => setShowCreate(false)}>
          <div style={{
            background: '#e8f4fd', border: '1px solid #3498db', borderRadius: '8px',
            padding: '10px 14px', fontSize: '13px', color: '#1a5276', marginBottom: '18px',
          }}>
            📧 Un email sera automatiquement envoyé à l'utilisateur avec un lien pour créer son mot de passe.
          </div>
          <Field label="Nom d'utilisateur" type="text" value={newUser.username}
            onChange={e => setNewUser({ ...newUser, username: e.target.value })}
            placeholder="ex: jean.dupont" />
          <Field label="Email" type="email" value={newUser.email}
            onChange={e => setNewUser({ ...newUser, email: e.target.value })}
            placeholder="jean@entreprise.fr" />
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Rôle</label>
            <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #ddd', fontSize: '14px', background: 'white' }}>
              {ROLES.map(r => <option key={r}>{r}</option>)}
            </select>
            <p style={{ fontSize: '12px', color: '#888', marginTop: '6px', marginBottom: 0 }}>
              {newUser.role === 'Admin'     && '🔴 Accès complet — gestion des droits, toutes les pages'}
              {newUser.role === 'Manager'   && '🔵 Accès aux modules métier — lecture et modification'}
              {newUser.role === 'Opérateur' && '🟢 Accès limité — consultation uniquement'}
            </p>
          </div>
          {createError && <p style={{ color: '#e74c3c', fontSize: '13px', margin: '0 0 12px' }}>⚠ {createError}</p>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setShowCreate(false)}>Annuler</Btn>
            <Btn variant="success" onClick={handleCreate}>Créer et envoyer l'invitation</Btn>
          </div>
        </Modal>
      )}

      {/* ── Modal : Réinitialiser mot de passe ── */}
      {showReset && (
        <Modal title="🔑 Réinitialiser le mot de passe" onClose={() => setShowReset(null)}>
          <p style={{ color: '#555', fontSize: '14px', marginBottom: '20px' }}>
            Un email sera envoyé à <strong>{showReset.email}</strong> avec un lien pour définir un nouveau mot de passe.<br />
            Le lien sera valable <strong>24 heures</strong>.
          </p>
          {resetError && <p style={{ color: '#e74c3c', fontSize: '13px' }}>⚠ {resetError}</p>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setShowReset(null)}>Annuler</Btn>
            <Btn variant="primary" onClick={() => handleResetPassword(showReset)}>Envoyer le lien</Btn>
          </div>
        </Modal>
      )}

      {/* ── Modal : Changer rôle ── */}
      {showRole && (
        <Modal title={`✏️ Changer le rôle de ${showRole.username}`} onClose={() => setShowRole(null)}>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
            Rôle actuel : <RoleBadge role={showRole.role} />
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {ROLES.filter(r => r !== showRole.role).map(r => (
              <button key={r} onClick={() => handleRoleChange(showRole, r)} style={{
                padding: '12px 16px', borderRadius: '8px', cursor: 'pointer',
                border: `2px solid ${ROLE_COLORS[r]?.border || '#ccc'}`,
                background: ROLE_COLORS[r]?.bg || '#f5f5f5',
                color: ROLE_COLORS[r]?.color || '#333',
                fontWeight: 600, fontSize: '14px', textAlign: 'left',
              }}>
                <RoleBadge role={r} />
                <span style={{ marginLeft: '10px', fontSize: '13px', fontWeight: 400, color: '#666' }}>
                  {r === 'Admin'     && '— Accès complet'}
                  {r === 'Manager'   && '— Accès modules métier'}
                  {r === 'Opérateur' && '— Consultation uniquement'}
                </span>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Btn variant="ghost" onClick={() => setShowRole(null)}>Annuler</Btn>
          </div>
        </Modal>
      )}

      {/* ── Modal : Confirmer suppression ── */}
      {showConfirmDelete && (
        <Modal title="⚠️ Confirmer la suppression" onClose={() => setShowConfirmDelete(null)}>
          <p style={{ color: '#555', fontSize: '14px', marginBottom: '20px' }}>
            Voulez-vous vraiment supprimer <strong>{showConfirmDelete.username}</strong> ?<br />
            <span style={{ color: '#e74c3c', fontSize: '13px' }}>Cette action est irréversible.</span>
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setShowConfirmDelete(null)}>Annuler</Btn>
            <Btn variant="danger" onClick={() => handleDelete(showConfirmDelete)}>Supprimer définitivement</Btn>
          </div>
        </Modal>
      )}

    </div>
  );
};

export default Gestiondesdroits;